<template>
  <v-container>
    <v-form ref="form" v-model="isValid">
      <v-row>
        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Financial Year</p>
          </v-col>
          <v-col class="pa-0 ma-0">
            <v-autocomplete
              v-model="form.financial_year"
              :items="financial_years"
              item-text="year"
              item-value="id"
              placeholder="Financial Year"
              :rules="[required]"
              outlined
              dense
              clearable
            ></v-autocomplete>
          </v-col>
        </v-col>

        <v-col cols="12" md="6"></v-col>

        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Branch</p>
          </v-col>
          <v-col class="pa-0 ma-0">
            <v-autocomplete
              v-model="form.branch"
              :items="branches"
              item-text="name"
              item-value="id"
              placeholder="Branch"
              :rules="[required]"
              outlined
              dense
              clearable
            ></v-autocomplete>
            <p v-if="branchError" class="error--text text-caption">
              {{ branchError }}
            </p>
          </v-col>
        </v-col>

        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Date</p>
          </v-col>
          <v-text-field
            v-model="form.date"
            placeholder="Date"
            :rules="[required]"
            type="date"
            outlined
            dense
          ></v-text-field>
        </v-col>

        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Credit Account</p>
          </v-col>
          <v-col class="pa-0 ma-0">
            <v-autocomplete
              v-model="form.crAccount"
              :items="craccounts"
              item-text="title"
              item-value="id"
              placeholder="Search Credit Account"
              :rules="[required]"
              outlined
              dense
              clearable
              :loading="isFetchingCrAccounts"
              :filter="() => true"
              @update:search-input="
                (searchTerm) => onSearchAccount(searchTerm, 'cr')
              "
            ></v-autocomplete>
          </v-col>
        </v-col>

        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Debit Account</p>
          </v-col>
          <v-col class="pa-0 ma-0">
            <v-autocomplete
              v-model="form.drAccount"
              :items="draccounts"
              item-text="title"
              item-value="id"
              placeholder="Search Debit Account"
              :rules="[required]"
              outlined
              dense
              clearable
              :loading="isFetchingDrAccounts"
              :filter="() => true"
              @update:search-input="
                (searchTerm) => onSearchAccount(searchTerm, 'dr')
              "
            ></v-autocomplete>
          </v-col>
        </v-col>

        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Amount</p>
          </v-col>
          <v-text-field
            v-model="form.amount"
            placeholder="Amount"
            :rules="[required, isPositiveNumber]"
            outlined
            dense
          ></v-text-field>
        </v-col>

        <v-col cols="12">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Remarks</p>
          </v-col>
          <v-textarea
            v-model="form.remarks"
            label="Remarks"
            outlined
            dense
          ></v-textarea>
        </v-col>
      </v-row>
      <v-row class="mt-4">
        <v-btn
          v-if="hasPermission('GL_Transactions.create')"
          color="success"
          @click="submitForm"
          :disabled="!isValid"
        >
          Add Transaction
        </v-btn>
        <v-btn color="grey" class="ml-2" @click="resetForm">
          Reset
        </v-btn>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import { GLTransactionService } from "@/services/GLTransaction-service.js";
import { CommonService } from "@/services/common-service.js";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

export default {
  name: "GLTransactionAddingForm",
  data() {
    return {
      branchError: "",
      isValid: false,
      form: {
        financial_year: null,
        branch: null,
        crAccount: null,
        drAccount: null,
        amount: null,
        date: new Date().toISOString().substr(0, 10),
        remarks: "",
      },
      branches: [],
      craccounts: [],
      draccounts: [],
      financial_years: [],
      isFetchingCrAccounts: false,
      isFetchingDrAccounts: false,
      accountSearchTimeout: null, // For debouncing
    };
  },
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
  },
  methods: {
    required(value) {
      return !!value || "This field is required.";
    },
    isPositiveNumber(value) {
      return value > 0 || "Amount must be a positive number.";
    },

    async fetchDropdownData() {
      try {
        const financialYearsResponse = await CommonService.getFinancialYears();
        this.financial_years = financialYearsResponse.data;
        const branchesResponse = await CommonService.getBranches();
        this.branches = branchesResponse.data;
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    },

    async onSearchAccount(searchTerm, type) {
      if (!this.form.branch) {
        this.branchError = "Branch must be selected before searching accounts.";
        console.warn("Branch must be selected before searching accounts.");
        return;
      }
      this.branchError = "";

      try {
        clearTimeout(this.accountSearchTimeout);
        if (type === "cr") {
          this.isFetchingCrAccounts = true;

          this.accountSearchTimeout = setTimeout(async () => {
            const response = await GLTransactionService.getAccountsByBranch(
              this.form.branch,
              searchTerm
            );
            this.craccounts = response.data.data;
          }, 300);
        } else if (type === "dr") {
          this.isFetchingDrAccounts = true;

          this.accountSearchTimeout = setTimeout(async () => {
            const response = await GLTransactionService.getAccountsByBranch(
              this.form.branch,
              searchTerm
            );
            this.draccounts = response.data.data;
          }, 300);
        }
      } catch (error) {
        console.error("Error fetching accounts:", error);
      } finally {
        if (type === "cr") {
          this.isFetchingCrAccounts = false;
        } else if (type === "dr") {
          this.isFetchingDrAccounts = false;
        }
      }
    },

    async submitForm() {
      if (this.$refs.form.validate()) {
        try {
          await GLTransactionService.createGLTransaction(this.form);
          this.resetForm();
          this.$router.push({ name: "GLTransactionList" });
        } catch (error) {
          console.error("Error submitting the form:", error);
        }
      }
    },
    resetForm() {
      this.$refs.form.reset();
      this.form = {
        branch: null,
        crAccount: null,
        drAccount: null,
        amount: null,
        date: new Date().toISOString().substr(0, 10),
        remarks: "",
      };
      this.accounts = [];
    },
  },
  mounted() {
    this.fetchDropdownData();
  },
};
</script>
